<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import {
  isBlockImage,
  type BlockImage,
  type CardTemplateProps,
  type Translated,
} from '@/api/types';
import { computed, type ComputedRef } from 'vue';
import { getBlobUrl } from '@/api/blob';
import { translateBlockValue } from '@/i18n';
import { type supported_locale } from '@/config';
import TagRenderer from '@/components/TagRenderer.vue';

const { t, locale } = useI18n();

const props = defineProps<CardTemplateProps>();

const productImage: ComputedRef<
  (Pick<Translated<BlockImage>, 'alt'> & { url: string }) | undefined
> = computed(() => {
  const productImageField = props.block.content?.['productimage'];

  if (productImageField && isBlockImage(productImageField)) {
    const translatedField = translateBlockValue(
      productImageField,
      locale.value as supported_locale,
      'image',
    );
    return {
      ...translatedField,
      url: getBlobUrl(
        {
          blockId: props.block.blockId,
          versionId: props.block.currentVersionId,
        },
        productImageField.name,
      ),
    };
  }
  return props.block.ownerOrg?.logo
    ? {
        alt: t('block.ownerLogo'),
        url: props.block.ownerOrg.logo,
      }
    : undefined;
});
</script>
<template>
  <div
    class="hover:bg-gnist-light-gray flex h-full flex-col break-words rounded-md bg-gnist-white p-6 text-gnist-black shadow-sm transition duration-300 ease-in-out hover:shadow-lg lg:w-[20rem]"
  >
    <div class="flex justify-between gap-4">
      <div class="flex items-center gap-2">
        <div
          :class="`bg-gnist-${block.category?.color ?? 'gray'}`"
          class="h-4 w-4 rounded-full"
        ></div>
        <p class="text-xs uppercase">
          {{ block.category?.name ?? '' }}
        </p>
      </div>
      <TagRenderer :tags="block.tags" />
    </div>

    <div class="my-6 flex max-h-14 flex-row">
      <div>
        <h2 class="text-subtitle">
          {{ block.name }}
        </h2>
        <p v-if="block.ownerOrg" class="mt-0.5 text-xs italic">
          {{ `${t('productCard.suppliedBy')} ${block.ownerOrg.name}` }}
        </p>
      </div>
    </div>

    <div v-if="productImage" class="mb-4 flex items-center justify-center">
      <img
        class="max-h-32 min-w-[33.333%] object-contain"
        :src="productImage.url"
        :alt="productImage.alt"
      />
    </div>

    <div v-else class="mb-4 ml-4 flex items-start">
      <p class="text-box grow">
        {{ block.description }}
      </p>
    </div>
  </div>
</template>
